/* eslint-disable no-magic-numbers */
/**
 * Spotlight effect utilities
 * Extracted from canvasUtils.ts to reduce file size and improve organization
 */

import type { SpotlightStyle } from '../types/interfaces';
import { DEFAULT_SPOTLIGHT_COLOR, MAP_CONSTANTS } from '../types/constants';

/**
 * Parse hex color string to RGB values
 */
function parseHexColor(hex: string): { r: number; g: number; b: number } {
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return { r, g, b };
}

// Parse default color once at module load
const DEFAULT_SPOTLIGHT_COLOR_RGB = parseHexColor(DEFAULT_SPOTLIGHT_COLOR);

/**
 * Parse color string to RGB values
 */
function parseColor(color: string): { r: number; g: number; b: number } {
  // Handle hex colors
  if (color.startsWith('#')) {
    return parseHexColor(color);
  }
  // Return default fallback color
  return DEFAULT_SPOTLIGHT_COLOR_RGB;
}

/**
 * Calculate pulsing intensity for spotlight animation
 */
function calculatePulseIntensity(
  style: SpotlightStyle,
  animationTime: number
): number {
  if (!style.animation.enabled) {
    return 1.0;
  }

  const speed = MAP_CONSTANTS.BASE_PULSE_SPEED * style.animation.speed;
  const time = animationTime * speed;

  // Create smooth pulsing intensity
  const rawPulse = Math.sin(time) * 0.5 + 0.5; // 0 to 1
  const range = style.animation.maxOpacity - style.animation.minOpacity;
  return (
    style.animation.minOpacity + rawPulse * range * style.animation.intensity
  );
}

/**
 * Create spotlight gradient with customizable pulsating effect
 */
export function createSpotlightGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  style: SpotlightStyle,
  animationTime: number = 0
): CanvasGradient {
  // Validate inputs to prevent canvas errors
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(radius) ||
    radius <= 0
  ) {
    throw new Error(
      `Invalid gradient parameters: x=${String(x)}, y=${String(y)}, radius=${String(radius)}. All values must be finite numbers and radius must be > 0.`
    );
  }

  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);

  const { r, g, b } = parseColor(style.color);
  const pulseIntensity = calculatePulseIntensity(style, animationTime);

  // Apply overall style opacity multiplier
  const finalOpacity = pulseIntensity * style.opacity;

  // Create gradient stops with calculated opacity - much denser at the base
  gradient.addColorStop(
    0,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 1.0))})`
  ); // Center - full intensity
  gradient.addColorStop(
    0.15,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.98))})`
  ); // Very dense near base
  gradient.addColorStop(
    0.3,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.92))})`
  ); // Still very strong at base
  gradient.addColorStop(
    0.5,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.7))})`
  ); // Maintain density longer
  gradient.addColorStop(
    0.7,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.4))})`
  );
  gradient.addColorStop(
    0.8,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.25))})`
  );
  gradient.addColorStop(
    1,
    `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(Math.min(1.0, finalOpacity * 0.08))})`
  ); // Edges - slightly stronger than before

  return gradient;
}

/**
 * Draw spotlight effect for current point
 */
export function drawSpotlight(
  ctx: CanvasRenderingContext2D,
  position: { x: number; y: number },
  direction: number, // Direction angle in radians
  style: SpotlightStyle,
  canvasSize: { width: number; height: number },
  animationTime: number = 0,
  zoom: number = 1.0
): void {
  if (!style.enabled) return;

  // Validate inputs before drawing to prevent errors during initial load
  if (
    !Number.isFinite(position.x) ||
    !Number.isFinite(position.y) ||
    !Number.isFinite(direction) ||
    !Number.isFinite(canvasSize.width) ||
    !Number.isFinite(canvasSize.height) ||
    canvasSize.width <= 0 ||
    canvasSize.height <= 0
  ) {
    // Skip drawing if canvas isn't properly initialized yet
    return;
  }

  // Calculate spotlight radius - scales with map zoom for consistent appearance
  const fixedRadius = 47; // Fixed radius matching default map size (261px × 0.18 = ~47px)
  const radiusScale = 0.5;
  // Use square root scaling to reduce size more aggressively at higher zoom levels
  const zoomScale = Math.sqrt(zoom);
  const radius = fixedRadius * style.size * zoomScale * radiusScale; // Scale with sqrt of zoom level

  // Scale down the visual angle (width) for larger FOV angles to prevent spotlight from dominating the map
  const maxVisualAngle = 85;
  const visualAngle = Math.min(style.angle, maxVisualAngle);

  // Convert visual angle from degrees to radians
  const angleRadians = visualAngle * MAP_CONSTANTS.DEGREES_TO_RADIANS_FACTOR;

  // Offset the spotlight start position slightly forward to avoid overlapping station icon
  const startOffset = 2; // Distance to push spotlight away from station center (reduced for closer positioning)
  const spotlightStartX = position.x + Math.cos(direction) * startOffset;
  const spotlightStartY = position.y - Math.sin(direction) * startOffset;

  // Calculate spotlight cone points from the offset position
  const leftAngle = direction - angleRadians / 2;
  const rightAngle = direction + angleRadians / 2;

  const leftX = spotlightStartX + Math.cos(leftAngle) * radius;
  const leftY = spotlightStartY - Math.sin(leftAngle) * radius;
  const rightX = spotlightStartX + Math.cos(rightAngle) * radius;
  const rightY = spotlightStartY - Math.sin(rightAngle) * radius;

  try {
    // Create and apply gradient from the offset position
    const gradient = createSpotlightGradient(
      ctx,
      spotlightStartX,
      spotlightStartY,
      radius,
      style,
      animationTime
    );

    // Draw spotlight cone from the offset position
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(spotlightStartX, spotlightStartY);
    ctx.lineTo(leftX, leftY);
    ctx.lineTo(rightX, rightY);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();
  } catch (error) {
    // Log error but don't crash the application
    console.warn('Failed to draw spotlight:', {
      error,
      position,
      direction,
      canvasSize,
      spotlightStartX,
      spotlightStartY,
      radius,
    });
  }
}
